'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { SpartanProductCustomizePreviewInfo } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-info';
import { SpartanProductCustomizePreviewEdgeBackdrop } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/edge-backdrop';
import type { SpartanProductCustomizePreviewEdgeBackdropHandle } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/edge-backdrop/types';
import { SpartanProductCustomizePreviewCarousel } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/preview-carousel';
import type { SpartanCustomizePreviewTone } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/preview-tone';
import { SpartanProductCustomizePreviewThumbnails } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/thumbnails';
import type { SpartanProductCustomizePreviewMediaProps } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/types';
import styles from '@/app/lcv/components/sections/spartan/products/customize/styles.module.css';
import { SPARTAN_PRODUCT_CUSTOMIZE_TRACKING } from '@/app/lcv/components/sections/spartan/products/customize/tracking';
import type { SpartanCustomizePreviewOption, SpartanCustomizePreviewView } from '@/app/lcv/components/sections/spartan/products/customize/types';
import { getSpartanRevealElementProps, SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET, SPARTAN_REVEAL_PRESET } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SPARTAN_PRODUCT_CUSTOMIZE_PREVIEW_VIEW_LABELS } from '@/app/lcv/contents/spartan/products/shared/customize-common-fields.data';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
const VEHICLE_PREVIEW_VIEWS: SpartanCustomizePreviewView[] = ['front', 'rear'];
const getThumbnailOptions = (options: SpartanCustomizePreviewOption[]): SpartanCustomizePreviewOption[] => {
  const vehicleOptions = VEHICLE_PREVIEW_VIEWS.map(view => options.find(option => option.view === view) ?? {
    view,
    src: '',
    label: SPARTAN_PRODUCT_CUSTOMIZE_PREVIEW_VIEW_LABELS[view]
  });
  const interiorOption = options.find(option => option.view === 'interior');
  return interiorOption ? [...vehicleOptions, interiorOption] : vehicleOptions;
};
export const SpartanProductCustomizePreviewMedia = t0 => {
  const $ = _c(72);
  const {
    modelName,
    modelLabel,
    totalPrice,
    compareUrl,
    calculatorUrl,
    previewTone: t1,
    isDarkStudio: t2,
    previewOptions,
    thumbnailPreviewOptions: t3,
    activeView,
    emblaRef,
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    onSelectView,
    pendingPreviewOptions: t4,
    pendingThumbnailPreviewOptions: t5,
    onPreviewImageReady,
    onPreviewImageError,
    onThumbnailPreviewImageReady,
    onThumbnailPreviewImageError
  } = t0;
  const previewTone = t1 === undefined ? "light" : t1;
  const isDarkStudio = t2 === undefined ? false : t2;
  const thumbnailPreviewOptions = t3 === undefined ? previewOptions : t3;
  let t6;
  if ($[0] !== t4) {
    t6 = t4 === undefined ? [] : t4;
    $[0] = t4;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const pendingPreviewOptions = t6;
  let t7;
  if ($[2] !== t5) {
    t7 = t5 === undefined ? [] : t5;
    $[2] = t5;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const pendingThumbnailPreviewOptions = t7;
  const isInteriorView = activeView === "interior";
  const isDarkChrome = isInteriorView || isDarkStudio;
  let t8;
  if ($[4] !== thumbnailPreviewOptions) {
    t8 = getThumbnailOptions(thumbnailPreviewOptions);
    $[4] = thumbnailPreviewOptions;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const resolvedThumbnailOptions = t8;
  const edgeBackdropRef = useRef(null);
  const hadPreviewOptionsRef = useRef(previewOptions.length > 0);
  let t10;
  let t9;
  if ($[6] !== previewOptions.length || $[7] !== previewTone) {
    t9 = () => {
      const hasPreviewOptions = previewOptions.length > 0;
      if (!hadPreviewOptionsRef.current && hasPreviewOptions) {
        edgeBackdropRef.current?.setTo(previewTone);
      }
      hadPreviewOptionsRef.current = hasPreviewOptions;
    };
    t10 = [previewOptions.length, previewTone];
    $[6] = previewOptions.length;
    $[7] = previewTone;
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useEffect(t9, t10);
  let t11;
  if ($[10] !== onPreviewImageReady) {
    t11 = option => {
      safeCall(onPreviewImageReady, option);
    };
    $[10] = onPreviewImageReady;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const handlePreviewImageReady = t11;
  let t12;
  if ($[12] !== onPreviewImageError) {
    t12 = option_0 => {
      safeCall(onPreviewImageError, option_0);
    };
    $[12] = onPreviewImageError;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const handlePreviewImageError = t12;
  let t13;
  if ($[14] !== onThumbnailPreviewImageReady) {
    t13 = option_1 => {
      safeCall(onThumbnailPreviewImageReady, option_1);
    };
    $[14] = onThumbnailPreviewImageReady;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const handleThumbnailImageReady = t13;
  let t14;
  if ($[16] !== onThumbnailPreviewImageError) {
    t14 = option_2 => {
      safeCall(onThumbnailPreviewImageError, option_2);
    };
    $[16] = onThumbnailPreviewImageError;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const handleThumbnailImageError = t14;
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = (incomingTone, t16) => {
      const shouldCrossfade = t16 === undefined ? true : t16;
      if (!incomingTone) {
        return;
      }
      if (shouldCrossfade) {
        edgeBackdropRef.current?.fadeTo(incomingTone);
        return;
      }
      edgeBackdropRef.current?.setTo(incomingTone);
    };
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const handleVehicleSwapStart = t15;
  const t16 = isDarkChrome ? "dark" : "light";
  const t17 = isInteriorView ? "solid" : "transparent";
  let t18;
  if ($[19] !== calculatorUrl || $[20] !== compareUrl || $[21] !== modelLabel || $[22] !== modelName || $[23] !== t16 || $[24] !== t17 || $[25] !== totalPrice) {
    t18 = <SpartanProductCustomizePreviewInfo placement="desktop" modelName={modelName} modelLabel={modelLabel} totalPrice={totalPrice} compareUrl={compareUrl} calculatorUrl={calculatorUrl} tone={t16} surface={t17} />;
    $[19] = calculatorUrl;
    $[20] = compareUrl;
    $[21] = modelLabel;
    $[22] = modelName;
    $[23] = t16;
    $[24] = t17;
    $[25] = totalPrice;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const previewInfo = t18;
  let t19;
  if ($[27] !== previewTone) {
    t19 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.mediaColumn,
      "preview-tone": previewTone
    });
    $[27] = previewTone;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  let t21;
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = getSpartanRevealElementProps({
      preset: SPARTAN_REVEAL_PRESET.media,
      scrollOffset: SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET
    });
    t21 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.preview
    });
    t22 = cn(styles.previewRoundedClip, "relative overflow-hidden rounded-lg");
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
  } else {
    t20 = $[29];
    t21 = $[30];
    t22 = $[31];
  }
  const t23 = !isInteriorView && previewOptions.length > 0;
  let t24;
  if ($[32] !== previewTone || $[33] !== t23) {
    t24 = <SpartanProductCustomizePreviewEdgeBackdrop ref={edgeBackdropRef} tone={previewTone} holdForImage={t23} />;
    $[32] = previewTone;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== previewOptions.length) {
    t25 = !previewOptions.length && <div className={cn(styles.previewSurface, "aspect-16-9 w-full")} aria-hidden="true" {...createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.previewEmpty
    })} />;
    $[35] = previewOptions.length;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== activeView || $[38] !== emblaRef || $[39] !== handlePreviewImageError || $[40] !== handlePreviewImageReady || $[41] !== isDarkChrome || $[42] !== modelLabel || $[43] !== nextBtnDisabled || $[44] !== onNextButtonClick || $[45] !== onPrevButtonClick || $[46] !== pendingPreviewOptions || $[47] !== prevBtnDisabled || $[48] !== previewOptions || $[49] !== previewTone) {
    t26 = !!previewOptions.length && <SpartanProductCustomizePreviewCarousel modelLabel={modelLabel} previewTone={previewTone} isDarkChrome={isDarkChrome} previewOptions={previewOptions} pendingOptions={pendingPreviewOptions} activeView={activeView} emblaRef={emblaRef} isPrevDisabled={prevBtnDisabled} isNextDisabled={nextBtnDisabled} onPrev={onPrevButtonClick} onNext={onNextButtonClick} onImageReady={handlePreviewImageReady} onImageError={handlePreviewImageError} onVehicleSwapStart={handleVehicleSwapStart} />;
    $[37] = activeView;
    $[38] = emblaRef;
    $[39] = handlePreviewImageError;
    $[40] = handlePreviewImageReady;
    $[41] = isDarkChrome;
    $[42] = modelLabel;
    $[43] = nextBtnDisabled;
    $[44] = onNextButtonClick;
    $[45] = onPrevButtonClick;
    $[46] = pendingPreviewOptions;
    $[47] = prevBtnDisabled;
    $[48] = previewOptions;
    $[49] = previewTone;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.previewInfoDesktop
    });
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== previewInfo) {
    t28 = <div className="relative z-10 hidden lg:block" {...t27}>{previewInfo}</div>;
    $[52] = previewInfo;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t24 || $[55] !== t25 || $[56] !== t26 || $[57] !== t28) {
    t29 = <div className="relative order-1 rounded-lg lg:col-span-2 lg:row-start-1" {...t20} {...t21}><div className={t22}>{t24}{t25}{t26}{t28}</div></div>;
    $[54] = t24;
    $[55] = t25;
    $[56] = t26;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== activeView || $[60] !== handleThumbnailImageError || $[61] !== handleThumbnailImageReady || $[62] !== isDarkStudio || $[63] !== onSelectView || $[64] !== pendingThumbnailPreviewOptions || $[65] !== previewTone || $[66] !== resolvedThumbnailOptions) {
    t30 = !!resolvedThumbnailOptions.length && <SpartanProductCustomizePreviewThumbnails options={resolvedThumbnailOptions} pendingOptions={pendingThumbnailPreviewOptions} previewTone={previewTone} isDarkStudio={isDarkStudio} activeView={activeView} onSelectView={onSelectView} onImageReady={handleThumbnailImageReady} onImageError={handleThumbnailImageError} />;
    $[59] = activeView;
    $[60] = handleThumbnailImageError;
    $[61] = handleThumbnailImageReady;
    $[62] = isDarkStudio;
    $[63] = onSelectView;
    $[64] = pendingThumbnailPreviewOptions;
    $[65] = previewTone;
    $[66] = resolvedThumbnailOptions;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t19 || $[69] !== t29 || $[70] !== t30) {
    t31 = <div className={styles.previewToneScope} {...t19}>{t29}{t30}</div>;
    $[68] = t19;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  return t31;
};